import { inject, Injectable } from '@angular/core'
import { Dispatch } from '@ngxs-labs/dispatch-decorator'
import { Select } from '@ngxs/store'
import {
  BehaviorSubject,
  catchError,
  filter,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  take,
} from 'rxjs'
import { LoginFromStorage, Logout, SetToken } from './auth.actions'
import { DecodedToken } from '@wm-front/shared/interfaces'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { AuthState } from './auth.state'
import { DatadogService, FeatureFlagService } from '@wm-front/core-tools'
// eslint-disable-next-line @nx/enforce-module-boundaries
import { getDatadogUser, getFeatureFlagUser } from '@wm-front/shared/utils'
import { ClerkService } from '@wm-front/clerk'
import { ENVIRONMENT, Environment } from '@wm-front/shared/environment'
import { I18nService } from '@wm-front/core'
import { frFR, enGB, ptPT, zhCN } from '@clerk/localizations'
import { AdminInfoQueryGQL } from './graphql/admin-info.types'

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class AuthFacadeService {
  /** Return true if the user is authenticated */
  @Select(AuthState.isAuthenticated)
  public isAuthenticated$: Observable<boolean>

  /** Return the username of the logged in user */
  @Select(AuthState.username)
  public username$: Observable<string>

  /** Return the access token */
  @Select(AuthState.accessToken)
  public accessToken$: Observable<string | null>

  /** Return the decodedToken */
  @Select(AuthState.decodedToken)
  public decodedToken$: Observable<DecodedToken | null>

  public env = inject<Environment>(ENVIRONMENT)

  /**
   * Check wether or not clerk is ready
   */
  public clerkReady$: Observable<boolean> = new BehaviorSubject<boolean>(
    false
  ).pipe(
    switchMap(() => {
      if (this.env.clerk) return this._clerk.client$
      return of(true)
    }),
    filter((client) => !!client),
    take(1),
    map(() => true)
  )

  /** Fetches admin info (territories, subscription) when username changes */
  // @ts-expect-error -- accessToken$ is defined by the @Select decorator, not a field initializer (TS2729)
  public adminInfo$ = this.accessToken$.pipe(
    filter((isAuthenticated) => !!isAuthenticated),
    switchMap(() => this._adminInfoGql.fetch()),
    map(({ data }) => {
      if (data.me?.__typename === 'Admin') {
        return {
          territories: data.me.territories,
          subscriptionInfo: data.me.subscriptionInfo,
        }
      }
      return { territories: [], subscriptionInfo: null }
    }),
    catchError((e) => {
      console.error('Error fetching admin info', e)
      return of({ territories: [], subscriptionInfo: null })
    }),
    shareReplay(1)
  )

  protected _adminInfoGql = inject(AdminInfoQueryGQL)

  protected _datadogService = inject(DatadogService)

  protected _featureFlagService = inject(FeatureFlagService)

  protected _i18n = inject(I18nService)

  protected _clerk = inject(ClerkService)

  protected _clerkLanguages = {
    'fr-FR': frFR,
    'en-GB': enGB,
    'pt-PT': ptPT,
    'zh-Hant-HK': zhCN,
  }

  constructor() {
    if (this.env.clerk) {
      this._clerk.__init({
        publishableKey: this.env.clerk?.publishableKey,
        localization:
          this._clerkLanguages[
            (localStorage.getItem('wm-locale') as string) || this._i18n.language
          ] || this._clerkLanguages['en-GB'],
        appearance: {
          elements: {
            cardBox: {
              boxShadow: 'none',
              borderRadius: '4px',
            },
            headerTitle: {
              color: '#272c6c',
              fontSize: '28px',
              fontFamily: 'SanFranciscoSemiBold',
            },
            socialButtonsBlockButton: {
              height: '48px',
              borderRadius: '4px',
            },
            socialButtonsBlockButtonText: {
              fontSize: '14px',
              letterSpacing: '0.25px',
              fontFamily: 'SanFranciscoMedium',
            },
            dividerText: {
              textTransform: 'uppercase',
            },
            form: {
              gap: '1.5rem',
            },
            formFieldInput: {
              height: '50px',
              paddingTop: '24px',
              paddingBottom: '24px',
              border: '1px solid #ccc',
              fontFamily: 'SanFranciscoRegular',
              fontSize: '14px',
              letterSpacing: '0.13px',
              lineHeight: '20px',
              borderRadius: '4px',
            },
            formButtonPrimary: {
              height: '48px',
              boxShadow: 'none !important',
              borderRadius: '4px',
            },
            button: {
              fontSize: '14px',
              letterSpacing: '0.25px',
              fontFamily: 'SanFranciscoMedium',
              lineHeight: '16px',
            },
            footerActionLink: {
              fontSize: '14px',
              color: 'rgb(33, 33, 33)',
            },
          },
          variables: {
            colorPrimary: '#f1653c',
          },
        },
      })
    }
    this.initDatadog()
    this.initFeatureFlag()
    this._clerk.session$.subscribe(async (session) => {
      if (session) {
        this.setToken((await session.getToken())!, 'clerk')
      }
    })
  }

  /** Dispatch set token action
   * @param { string } token the jwt to set in the state
   */
  @Dispatch()
  public setToken(
    token: string,
    mode: 'clerk' | 'legacy' = 'legacy'
  ): SetToken {
    return new SetToken(token, mode)
  }

  /** Dispatch loginFromStorage action */
  @Dispatch()
  public loginFromStorage(): LoginFromStorage {
    return new LoginFromStorage()
  }

  /** Dispatch logout action */
  @Dispatch()
  public logout(): Logout {
    return new Logout()
  }

  /** Init datadog user information */
  private initDatadog(): void {
    this.decodedToken$
      .pipe(untilDestroyed(this))
      .subscribe((decodedToken: DecodedToken | null) => {
        this._datadogService.setUser(getDatadogUser(decodedToken))
      })
  }

  /** Init feature flag */
  private initFeatureFlag(): void {
    this.decodedToken$
      .pipe(untilDestroyed(this))
      .subscribe((decodedToken: DecodedToken | null) => {
        this._featureFlagService.setUser(getFeatureFlagUser(decodedToken))
      })
  }
}
